/**
 * OrderBottomSheet Component
 * Bottom sheet showing order items with quantity controls and item removal
 * Supports swipe-down-to-close gesture
 */

import { Minus, Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { OrderItem } from '../../../types/agenticChat';
import type { Language } from './VoiceOrderingContainer';
import { formatPrice, getArabicName, getItemName } from './recommendationHelpers';

interface OrderBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  language: Language;
  items: OrderItem[];
  total: number;
  onQuantityUpdate?: (itemName: string, quantity: number) => void;
  onItemRemove?: (itemName: string) => void;
}

export function OrderBottomSheet({ 
  isOpen, 
  onClose, 
  language,
  items,
  total,
  onQuantityUpdate,
  onItemRemove,
}: OrderBottomSheetProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [dragDistance, setDragDistance] = useState(0);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  // Use AI's total_price if provided (in EGP), otherwise calculate from items
  const totalPriceEGP = total > 0 
    ? total  // AI provides total_price in EGP
    : items.reduce((sum, item) => sum + item.price * item.quantity, 0); // Calculate from items (prices in EGP)

  // Reset drag state when sheet opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIsDragging(false);
      setDragDistance(0);
    }
  }, [isOpen]);

  // Prevent body scroll when bottom sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Touch event handlers for swipe-down-to-close
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isOpen) return;
    const touch = e.touches[0];
    setStartY(touch.clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !isOpen) return;
    const touch = e.touches[0];
    const deltaY = touch.clientY - startY;
    
    // Only allow downward dragging
    if (deltaY > 0) {
      setDragDistance(deltaY);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    // Close if dragged down more than 100px
    const threshold = 100;
    if (dragDistance > threshold) {
      onClose();
    } else {
      // Snap back
      setDragDistance(0);
    }
  };

  // Mouse event handlers for desktop drag support
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isOpen) return;
    setStartY(e.clientY);
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !isOpen) return;
    const deltaY = e.clientY - startY;
    
    if (deltaY > 0) {
      setDragDistance(deltaY);
    }
  };

  const handleMouseUp = () => {
    handleTouchEnd();
  };

  if (!isOpen) {
    return null;
  }

  const translateY = isDragging ? dragDistance : 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center"
      style={{
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(8px)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Bottom Sheet Container */}
      <div
        ref={sheetRef}
        className="relative flex flex-col overflow-hidden rounded-t-3xl"
        style={{
          width: '100%',
          maxHeight: '90vh',
          height: 'auto',
          background: 'var(--card-bg)',
          animation: !isDragging ? 'slideUpFromBottom 300ms ease-out' : 'none',
          transform: `translateY(${translateY}px)`,
          transition: isDragging ? 'none' : 'transform 200ms ease-out',
          borderTopLeftRadius: '1.5rem',
          borderTopRightRadius: '1.5rem',
          borderBottomLeftRadius: '0',
          borderBottomRightRadius: '0',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag Handle Indicator */}
        <div
          ref={dragHandleRef}
          className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing"
          style={{ touchAction: 'pan-y' }} // Allow vertical panning (swipe down) but prevent horizontal scrolling
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div
            style={{
              width: '40px',
              height: '4px',
              borderRadius: '2px',
              background: 'var(--border)',
            }}
          />
        </div>

        {/* Content Container - Scrollable */}
        <div
          className="flex flex-col overflow-y-auto"
          style={{
            direction: language === 'ar' ? 'rtl' : 'ltr',
            maxHeight: 'calc(90vh - 80px)', // Account for drag handle and total section
          }}
        >
          {/* Header */}
          <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
            <h2
              className="text-xl font-bold"
              style={{ color: 'var(--text-primary)' }}
            >
              {language === 'en' ? 'Your Order' : 'طلبك'}
            </h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              {itemCount} {language === 'en' 
                ? (itemCount === 1 ? 'item' : 'items')
                : (itemCount === 1 ? 'عنصر' : 'عناصر')}
            </p>
          </div>

          {/* Order Items */}
          <div className="px-5 py-4 space-y-4">
            {(() => {
              const validItems = items.filter(item => item.quantity > 0);
              return validItems.length === 0 ? (
                <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                  <p>{language === 'en' ? 'Your order is empty' : 'طلبك فارغ'}</p>
                </div>
              ) : (
                validItems.map((item) => {
                // Extract name from OrderItem (may contain "Arabic | English" format)
                const itemName = getItemName(item.item, language);
                const itemNameForDisplay = language === 'ar' ? getArabicName(item.item) || itemName : itemName;
                const unitPriceEGP = item.price; // Price is already in EGP from AI order
                const lineTotalEGP = item.price * item.quantity;

                return (
                  <div
                    key={item.item_id}
                    className="flex gap-3 pb-4 border-b last:border-b-0"
                    style={{ borderColor: 'var(--border)' }}
                  >
                    {/* Item Image */}
                    <div
                      className="w-16 h-16 flex items-center justify-center rounded-md text-2xl flex-shrink-0"
                      style={{ background: 'var(--cream-medium)' }}
                    >
                      🍽️
                    </div>

                    {/* Item Details */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>
                        {itemNameForDisplay}
                      </h3>
                      <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>
                        {formatPrice(unitPriceEGP, language)} {language === 'en' ? 'each' : 'لكل'}
                      </p>

                      {/* Quantity Controls */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 border rounded-md" style={{ borderColor: 'var(--border)' }}>
                          <button
                            onClick={() => {
                              if (item.quantity > 1) {
                                const newQuantity = item.quantity - 1;
                                // Notify parent about quantity update (AI will handle the update)
                                if (onQuantityUpdate) {
                                  onQuantityUpdate(itemNameForDisplay, newQuantity);
                                }
                              }
                            }}
                            disabled={item.quantity <= 1}
                            className="flex h-8 w-8 items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-opacity-80"
                            style={{
                              background: item.quantity <= 1 ? 'transparent' : 'var(--accent-gold)',
                              color: item.quantity <= 1 ? 'var(--text-secondary)' : 'white',
                            }}
                            aria-label={language === 'en' ? 'Decrease quantity' : 'تقليل الكمية'}
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-8 text-center text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => {
                              if (item.quantity < 99) {
                                const newQuantity = item.quantity + 1;
                                // Notify parent about quantity update (AI will handle the update)
                                if (onQuantityUpdate) {
                                  onQuantityUpdate(itemNameForDisplay, newQuantity);
                                }
                              }
                            }}
                            disabled={item.quantity >= 99}
                            className="flex h-8 w-8 items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-opacity-80"
                            style={{
                              background: 'var(--accent-gold)',
                              color: 'white',
                            }}
                            aria-label={language === 'en' ? 'Increase quantity' : 'زيادة الكمية'}
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Line Total and Remove */}
                        <div className="flex items-center gap-3">
                          <p className="font-bold text-sm" style={{ color: 'var(--accent-gold)' }}>
                            {formatPrice(lineTotalEGP, language)}
                          </p>
                          <button
                            onClick={() => {
                              // Notify parent about item removal (AI will handle the removal)
                              if (onItemRemove) {
                                onItemRemove(itemNameForDisplay);
                              }
                            }}
                            className="flex h-8 w-8 items-center justify-center rounded-full transition-all hover:bg-opacity-80"
                            style={{
                              background: 'var(--error-light)',
                              color: 'white',
                            }}
                            aria-label={language === 'en' ? 'Remove item' : 'إزالة العنصر'}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
                })
              );
            })()}
          </div>
        </div>

        {/* Total Section - Fixed at Bottom */}
        {items.filter(item => item.quantity > 0).length > 0 && (
          <div
            className="border-t px-5 py-4"
            style={{
              borderColor: 'var(--border)',
              background: 'var(--card-bg)',
            }}
          >
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                {language === 'en' ? 'Total' : 'الإجمالي'}
              </span>
              <span className="text-xl font-bold" style={{ color: 'var(--accent-gold)' }}>
                {formatPrice(totalPriceEGP, language)}
              </span>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideUpFromBottom {
          from {
            opacity: 0;
            transform: translateY(100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
